import arClient from '../../../util/api_client';
import { isApiSuccess } from '../../../constants/platformStatusCodes';
import { sanitizeMediaName } from './mediaInput';

const ANALYZE_IMAGE_V2_ENDPOINT = '/media-model/analyze-image-v2';
const ANALYZE_VIDEO_V2_ENDPOINT = '/media-model/analyze-video-v2';

// Analysis triggers background ML jobs — allow a generous timeout (mirrors the
// legacy /media-model/upload call).
const ANALYZE_TIMEOUT_MS = 120000;

function queueAnalysis(endpoint, body) {
  return new Promise((resolve, reject) => {
    arClient.doApiCall(
      endpoint,
      body,
      (resp) => {
        if (isApiSuccess(resp)) {
          resolve(resp);
        } else {
          reject(new Error(resp?.error || resp?.message || 'Failed to analyze media.'));
        }
      },
      null, // doEncodeURIComponent
      { timeout: ANALYZE_TIMEOUT_MS }
    );
  });
}

/**
 * POST /media-model/analyze-image-v2 — queue an image for analysis. Async: the
 * request just queues the job; the result arrives later over the row's
 * broadcast channel (see MediaEchoListener).
 *
 * Provide the image via ONE of: `mediaId` (preferred for existing library rows)
 * or `dataPath`/`imageURL` (image URL — device uploads are pushed to S3 first,
 * see `uploadFileToS3Endpoint`). Optional fields are only sent when present so
 * the backend applies its own defaults.
 *
 * @param {object}  input
 * @param {number} [input.mediaId]    Library media id — preferred input.
 * @param {string} [input.dataPath]   Image URL.
 * @param {string} [input.imageURL]   Alias for `dataPath`.
 * @param {string} [input.name]       Optional display name.
 * @param {number} [input.requestId]  Optional audience builder request id.
 * @param {number} [input.segmentId]  Optional audience segment id.
 * @param {string} [input.platform]   Optional platform hint, e.g. 'instagram'.
 * @param {boolean}[input.save=false]     Overwrite the legacy v1 result — keep false for now.
 * @param {boolean}[input.useCache=true]  Let the backend serve a cached result.
 * @returns {Promise<object>} Resolves the API response on success; rejects on failure.
 */
export function analyzeImageV2({
  mediaId,
  dataPath,
  imageURL,
  name,
  requestId,
  segmentId,
  platform,
  save = true,
  useCache = false,
} = {}) {
  const body = { save: save ? 1 : 0, useCache: useCache ? 1 : 0 };
  if (mediaId != null) body.mediaId = mediaId;
  if (dataPath) body.dataPath = dataPath;
  if (imageURL) body.imageURL = imageURL;
  if (name) body.name = sanitizeMediaName(name); // backend errors on emoji
  if (requestId != null) body.requestId = requestId;
  if (segmentId != null) body.segmentId = segmentId;
  if (platform) body.platform = platform;

  return queueAnalysis(ANALYZE_IMAGE_V2_ENDPOINT, body);
}

/**
 * POST /media-model/analyze-video-v2 — queue a video for analysis. Async, same
 * as `analyzeImageV2` — no base64 upload for video (impractical size), URL
 * sources only.
 *
 * Provide the video via ONE of: `mediaId`, `dataPath`, or `videoURL` (device
 * uploads are pushed to S3 first, see `uploadFileToS3Endpoint`).
 *
 * @param {object}  input
 * @param {number} [input.mediaId]    Library media id — preferred input.
 * @param {string} [input.dataPath]   Video URL.
 * @param {string} [input.videoURL]   Alias for `dataPath`.
 * @param {string} [input.name]       Optional display name.
 * @param {number} [input.requestId]  Optional audience builder request id.
 * @param {number} [input.segmentId]  Optional audience segment id.
 * @param {string} [input.platform]   Optional platform hint, e.g. 'tiktok'.
 * @param {boolean}[input.save=false]     Overwrite the legacy v1 result — keep false for now.
 * @param {boolean}[input.useCache=true]  Let the backend serve a cached result.
 * @returns {Promise<object>} Resolves the API response on success; rejects on failure.
 */
export function analyzeVideoV2({
  mediaId,
  dataPath,
  videoURL,
  name,
  requestId,
  segmentId,
  platform,
  save = true,
  useCache = false,
} = {}) {
  const body = { save: save ? 1 : 0, useCache: useCache ? 1 : 0 };
  if (mediaId != null) body.mediaId = mediaId;
  if (dataPath) body.dataPath = dataPath;
  if (videoURL) body.videoURL = videoURL;
  if (name) body.name = sanitizeMediaName(name); // backend errors on emoji
  if (requestId != null) body.requestId = requestId;
  if (segmentId != null) body.segmentId = segmentId;
  if (platform) body.platform = platform;

  return queueAnalysis(ANALYZE_VIDEO_V2_ENDPOINT, body);
}

/**
 * Dispatch to `analyzeImageV2` or `analyzeVideoV2` based on `input.mediaType`
 * ('image' by default).
 *
 * @param {object} input See `analyzeImageV2`/`analyzeVideoV2`; also accepts `mediaType`.
 * @param {string} [input.mediaType='image'] 'image' or 'video'.
 */
export function analyzeMediaV2({ mediaType = 'image', ...rest } = {}) {
  return mediaType === 'video' ? analyzeVideoV2(rest) : analyzeImageV2(rest);
}

export default analyzeMediaV2;
